<template>
  <ValidationObserver ref="basicDetails">
    <ValidationProvider
      v-slot="{ errors }"
      name="interviewFormats"
      rules="required"
    >
      <h1 class="mt-4 mb-3">Basic Details</h1>
      <Select
        label="Interview Format"
        autocomplete
        :error-messages="showValidationErrors ? errors : ''"
        v-model="interviewFormatId"
        @input="val => handleChange('interviewFormatId', val)"
        :disabled="isFetchingInterviewFormats"
        :items="interviewFormats"
        item-text="interviewName"
        item-value="id"
        placeholder="Select Interview Format"
        chips
        small-chips
        :loading="isFetchingInterviewFormats"
      >
        <template v-slot:selection="{ data }">
          <StatusItem class="user-select-item">
            <template v-slot:icon>
              <v-icon>mdi-clipboard-text-outline</v-icon>
            </template>
            <div class="d-flex flex-column">
              <div>{{ data.item.interviewName | capitalize }}</div>
              <div class="secondary-text">{{ data.item.duration }} minutes</div>
            </div>
          </StatusItem>
        </template>
        <template v-slot:item="{ item }">
          <StatusItem class="user-select-item">
            <div class="d-flex flex-column">
              <div>{{ item.interviewName | capitalize }}</div>
              <div class="secondary-text">{{ item.duration }} minutes</div>
            </div>
          </StatusItem>
        </template>
      </Select>
    </ValidationProvider>
    <div class="mb-2">Additional Details for Candidate</div>
    <quill-editor
      @input="val => handleChange('candidateAdditionalDetails', val)"
      v-model="interviewSlotData.candidateAdditionalDetails"
      :options="editorOptions"
      class="mb-5"
    />
    <div class="mb-2">Additional Details for Interviewer(s)</div>
    <quill-editor
      @input="val => handleChange('interviewerAdditionalDetails', val)"
      v-model="interviewSlotData.interviewerAdditionalDetails"
      :options="editorOptions"
      class="mb-5"
    />
    <ValidationProvider
      v-if="!disableLocation"
      v-slot="{ errors }"
      rules="required"
    >
      <Select
        autocomplete
        v-model="interviewSlotData.location"
        @input="val => handleChange('location', val)"
        :error-messages="showValidationErrors ? errors : ''"
        height="50"
        class="mt-2"
        label="Location"
        placeholder="Select location"
        item-text="label"
        item-value="value"
        :items="formattedLocations"
        :loading="isFetchingLocations"
      />
    </ValidationProvider>
    <v-checkbox
      type="checkbox"
      hide-details
      @change="
        value => {
          handleChange('location', null), (disableLocation = value);
        }
      "
      label="Interview is taking place over the phone or virtually"
      v-model="disableLocation"
      class="shrink mt-0"
    />
  </ValidationObserver>
</template>

<script>
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import { FETCH_BOOKING_INTERVIEW_FORMATS } from "@/store/modules/bookings/interviews/actions";
import { createNamespacedHelpers } from "vuex";
import StatusItem from "@/components/common/StatusItem";
import Select from "@/components/common/Select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { copyState } from "@/utils/helpers";
import { BOOKINGS_NAMESPACE } from "@/store/modules/bookings/actions";
import { IS_LOADING_BOOKING_CANDIDATES } from "@/store/modules/bookings/getters";
import { DEFAULT_TOOLBAR_OPTIONS } from "@/plugins/v-quill-editor";
import { DATA_NAMESPACE } from "@/store/modules/data";
import { FETCH_LOCATIONS } from "@/store/modules/data/actions";
import { getFormattedLocation } from "@/utils/locations";
import {
  GET_IS_FETCHING_DATA,
  GET_LOCATIONS
} from "@/store/modules/data/getters";

const {
  mapActions: mapInterviewActions,
  mapState: mapInterviewState
} = createNamespacedHelpers(INTERVIEWS_NAMESPACE);
const { mapGetters: mapBookingGetters } = createNamespacedHelpers(
  BOOKINGS_NAMESPACE
);
const {
  mapActions: mapDataActions,
  mapGetters: mapDataGetters
} = createNamespacedHelpers(DATA_NAMESPACE);

export default {
  components: {
    StatusItem,
    Select,
    ValidationProvider,
    ValidationObserver
  },
  created() {
    this.fetchLocations({ filter: { "not-primary": true } });
    this.fetchInterviewFormats({ bookingId: this.currentBookingId });
  },
  data() {
    return {
      disableLocation: false,
      interviewSlotData: {
        candidateAdditionalDetails: "",
        interviewerAdditionalDetails: "",
        location: ""
      },
      interviewFormatId: null,
      showValidationErrors: false,
      editorOptions: {
        modules: {
          toolbar: {
            container: DEFAULT_TOOLBAR_OPTIONS
          }
        }
      }
    };
  },
  computed: {
    formattedLocations() {
      return this.locations.map(location => ({
        label: getFormattedLocation(location),
        value: location
      }));
    },
    ...mapDataGetters({
      isFetchingLocations: GET_IS_FETCHING_DATA,
      locations: GET_LOCATIONS
    }),
    ...mapBookingGetters({
      isLoadingBookingCandidates: IS_LOADING_BOOKING_CANDIDATES
    }),
    ...mapInterviewState(
      copyState(["interviewFormats", "isFetchingInterviewFormats"])
    ),
    currentBookingId() {
      return this.$route.params.id;
    }
  },
  methods: {
    ...mapDataActions({
      fetchLocations: FETCH_LOCATIONS
    }),
    ...mapInterviewActions({
      fetchInterviewFormats: FETCH_BOOKING_INTERVIEW_FORMATS
    }),
    handleChange(key, value) {
      this.$emit("onChange", {
        key,
        value
      });
    },
    handleInterviewFormatChange(interviewFormatId) {
      this.interviewFormatId = interviewFormatId;
    },
    async validateForm() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.basicDetails.validate();
      return isValid;
    }
  }
};
</script>

<style lang="scss"></style>
